import React, { useState } from 'react';
import Pagination from 'react-js-pagination';
import { useSelector, useDispatch } from 'react-redux';
import { resetSearch } from '@store/reducers/search';
import { NavLink, useHistory, useLocation } from 'react-router-dom';
import { Table, Button, Badge } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { createUrlWithParam } from '../../utils/helper';
import { URI_INTERNAL } from '../../utils/pathLocations';
import { objectShowNumber, messageToast } from '../../utils/define';
import { ConfirmModal } from '../../modules/modal/ConfirmModal';
import { Get, DeleteSingle } from '../../utils/httpProvider';
import { POSITION_API } from '../../utils/apiUrl';
import ReactSpinner from '../../components/reactSpinner/ReactSpinner';

const PositionList = () => {
    const history = useHistory();
    const location = useLocation();
    const dispatch = useDispatch();
    const valueSearch = useSelector((state) => state.search.valueSearch);

    const [isLoading, setIsLoading] = useState(false);
    const [listData, setListData] = useState([]);
    const [totalRecord, setTotalRecord] = useState(0);

    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [search, setSearch] = useState('');
    const [isActiveFilter, setIsActiveFilter] = useState('');

    const [openConfirmDeleteModal, setOpenConfirmDeleteModal] = useState(false);
    const [idWantDelete, setIdWantDelete] = useState('');
    const [confirmDelMessage, setConfirmDelMessage] = useState('');

    React.useEffect(() => {
        const paramUrl = new URLSearchParams(location.search);
        const pPage = paramUrl.get('page');
        const pSize = paramUrl.get('size');
        const pSearch = paramUrl.get('search');
        const pIsActive = paramUrl.get('isActive');

        if (pPage != null && parseInt(pPage, 10) !== page) setPage(parseInt(pPage, 10));
        if (pSize != null && parseInt(pSize, 10) !== pageSize) setPageSize(parseInt(pSize, 10));
        if (pSearch != null && pSearch !== search) setSearch(pSearch);
        if (pIsActive != null && pIsActive !== isActiveFilter) setIsActiveFilter(pIsActive);
    }, [location.search]);

    const fetchPagingData = async (Page, Size, Search, IsActive) => {
        setIsLoading(true);
        const filter = {
            Keyword: Search || '',
            PageIndex: Page,
            PageSize: Size
        };
        if (IsActive === 'true') filter.IsActive = true;
        if (IsActive === 'false') filter.IsActive = false;

        try {
            const response = await Get(POSITION_API.GET_PAGING, null, filter);
            if (response && response.data) {
                setListData(response.data);
                setTotalRecord(response.totalRecords || 0);
            }
        } catch (error) {
            toast.error('Lỗi khi lấy danh sách vị trí!');
        } finally {
            setIsLoading(false);
        }
    };

    const replaceHistory = async (Page, Size, Search, IsActive) => {
        await fetchPagingData(Page, Size, Search, IsActive);
        history.replace(
            createUrlWithParam(URI_INTERNAL.POSITION_PATH, {
                page: Page,
                size: Size,
                search: Search,
                isActive: IsActive
            })
        );
    };

    React.useEffect(() => {
        if (valueSearch != null && valueSearch !== search) {
            setSearch(valueSearch);
        }
    }, [valueSearch]);

    React.useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            replaceHistory(page, pageSize, search, isActiveFilter);
        }, 300);

        return () => clearTimeout(delayDebounceFn);
    }, [page, pageSize, search, isActiveFilter]);

    React.useEffect(() => {
        return () => { dispatch(resetSearch()); };
    }, []);

    const changePage = (number) => {
        setPage(number);
    };

    const onChangeShowNumber = (e) => {
        setPageSize(parseInt(e.target.value, 10));
        setPage(1);
    };

    const deleteAction = (id, name) => {
        setIdWantDelete(id);
        setConfirmDelMessage(name);
        setOpenConfirmDeleteModal(true);
    };

    const acceptDelete = async () => {
        if (idWantDelete !== '') {
            setIsLoading(true);
            try {
                const result = await DeleteSingle(POSITION_API.DELETE, idWantDelete, null);
                if (result.ok || result.status === 200) {
                    toast.success(messageToast.messageToastDeleteSuccess);
                    fetchPagingData(page, pageSize, search, isActiveFilter);
                } else {
                    const data = await result.json();
                    toast.error(data?.message || messageToast.messageToastDeleteError);
                }
            } catch (error) {
                toast.error(messageToast.messageToastTryCatch);
            } finally {
                setIsLoading(false);
                setOpenConfirmDeleteModal(false);
            }
        }
    };

    const cancelDelete = () => {
        setIdWantDelete('');
        setOpenConfirmDeleteModal(false);
    };

    return (
        <div className="container-fluid">
            {isLoading && <ReactSpinner />}
            <div className="row">
                <div className="col-lg-12">
                    <div className="card">
                        <div className="card-header">
                            <h3 className="card-title">Danh mục Vị trí</h3>
                        </div>
                        <div className="card-body">
                            <div className="d-flex justify-content-between mb-3">
                                <form className="form-inline">
                                    <label className="mr-2">Hiển thị :</label>
                                    <select
                                        className="form-control mr-3"
                                        value={pageSize}
                                        onChange={(e) => onChangeShowNumber(e)}
                                    >
                                        {objectShowNumber.map((item, key) => (
                                            <option key={Number(key)} value={item.value}>
                                                {item.text}
                                            </option>
                                        ))}
                                    </select>
                                    <select
                                        className="form-control mr-3"
                                        value={isActiveFilter}
                                        onChange={(e) => {
                                            setIsActiveFilter(e.target.value);
                                            setPage(1);
                                        }}
                                    >
                                        <option value="">Tất cả trạng thái</option>
                                        <option value="true">Đang hoạt động</option>
                                        <option value="false">Ngừng hoạt động</option>
                                    </select>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Mã hoặc Tên vị trí..."
                                        value={search}
                                        onChange={(e) => {
                                            setSearch(e.target.value);
                                            setPage(1);
                                        }}
                                    />
                                </form>
                                <NavLink to={URI_INTERNAL.POSITION_PATH_ADD}>
                                    <Button variant="info">
                                        <i className="fas fa-plus"></i> Thêm mới
                                    </Button>
                                </NavLink>
                            </div>
                            <div className="dataTables_wrapper dt-bootstrap4">
                                <div className="row">
                                    <div className="col-sm-12">
                                        <Table striped bordered hover responsive>
                                            <thead>
                                                <tr role="row">
                                                    <th>STT</th>
                                                    <th>Mã vị trí</th>
                                                    <th>Tên vị trí</th>
                                                    <th>Mô tả</th>
                                                    <th className="text-center">Trạng thái</th>
                                                    <th className="text-center">Thao tác</th>
                                                </tr>
                                            </thead>
                                            {listData != null && listData.length > 0 ? (
                                                <tbody>
                                                    {listData.map((item, index) => (
                                                        <tr key={item.id} className="odd">
                                                            <td>
                                                                {index + 1 + (page - 1) * pageSize}
                                                            </td>
                                                            <td className="font-weight-bold text-primary">{item.code}</td>
                                                            <td className="font-weight-bold">{item.name}</td>
                                                            <td>{item.description}</td>
                                                            <td className="text-center">
                                                                {item.isActive ? (
                                                                    <Badge variant="success">Đang hoạt động</Badge>
                                                                ) : (
                                                                    <Badge variant="secondary">Ngừng hoạt động</Badge>
                                                                )}
                                                            </td>
                                                            <td className="text-center">
                                                                <Button
                                                                    variant="link"
                                                                    className="text-primary p-0 mr-3"
                                                                    title="Sửa"
                                                                    onClick={() => history.push(URI_INTERNAL.POSITION_PATH_EDIT.replace(':id', item.id))}
                                                                >
                                                                    <i className="fas fa-edit fs-5"></i>
                                                                </Button>
                                                                <Button
                                                                    variant="link"
                                                                    className="text-danger p-0"
                                                                    title="Xóa"
                                                                    onClick={() => deleteAction(item.id, item.name)}
                                                                >
                                                                    <i className="fas fa-trash fs-5"></i>
                                                                </Button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            ) : (
                                                <tbody>
                                                    <tr className="text-center">
                                                        <td colSpan="6">Không có dữ liệu</td>
                                                    </tr>
                                                </tbody>
                                            )}
                                        </Table>
                                    </div>
                                </div>
                                {totalRecord > pageSize && (
                                    <div className="row">
                                        <div className="col-sm-12 col-md-5"></div>
                                        <div className="col-sm-12 col-md-7 d-flex justify-content-end">
                                            <div className="dataTables_paginate paging_simple_numbers">
                                                <Pagination
                                                    activePage={page}
                                                    itemsCountPerPage={pageSize}
                                                    totalItemsCount={totalRecord}
                                                    pageRangeDisplayed={5}
                                                    itemClass="page-item"
                                                    linkClass="page-link"
                                                    onChange={changePage}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <ConfirmModal
                isOpen={openConfirmDeleteModal}
                message={`Bạn có chắc chắn muốn xóa Vị trí "${confirmDelMessage}"?`}
                handleAccept={acceptDelete}
                handleCancel={cancelDelete}
            />
        </div>
    );
};

export default PositionList;
